import { monthIso, newId, newResume } from "./model";
import type { Resume, SkillLevel } from "./types";

// A complete, realistic example resume: the builder's "see an example"
// content, and the fixture the layout tests start from.

export function exampleResume(templateId: string | null = null): Resume {
  const base = newResume({ title: "Example — Product Designer", templateId });
  const achievements = newId();
  const volunteering = newId();
  const interests = newId();
  return {
    ...base,
    personalDetails: {
      ...base.personalDetails,
      fullName: "Aanya Kapoor",
      headline: "Senior Product Designer",
      email: "aanya.kapoor@example.com",
      phone: "+91 98765 43210",
      location: "Bengaluru, India",
      linkedIn: "linkedin.com/in/aanyakapoor",
      website: "aanya.design",
      summary:
        "Product designer with 8+ years shipping consumer and fintech apps used by millions. I pair qualitative research with fast prototyping, and I measure design by what it moves: activation, retention and support load. Comfortable owning a product area end to end, from discovery to launch metrics.",
    },
    experience: [
      {
        id: newId(),
        jobTitle: "Senior Product Designer",
        company: "Razorpay",
        location: "Bengaluru",
        employmentType: "Full-time",
        startDate: monthIso(2021, 4),
        endDate: null,
        isCurrent: true,
        description: "Lead designer for merchant onboarding and the payments dashboard (12 engineers, 2 PMs).",
        bulletPoints: [
          "Redesigned merchant onboarding end to end, lifting activation from 41% to 63% in two quarters.",
          "Built the dashboard design system (180+ components) now used by 6 product teams.",
          "Cut payment-failure support tickets by 32% with clearer error states and recovery flows.",
          "Mentored 4 designers; introduced weekly critique and a research repository.",
        ],
      },
      {
        id: newId(),
        jobTitle: "Product Designer",
        company: "Swiggy",
        location: "Bengaluru",
        employmentType: "Full-time",
        startDate: monthIso(2018, 7),
        endDate: monthIso(2021, 3),
        isCurrent: false,
        description: "",
        bulletPoints: [
          "Designed the restaurant partner app used by 150k+ restaurants across 500 cities.",
          "Shipped order-batching flows that reduced average prep-time escalations by 18%.",
          "Ran 40+ usability sessions in five languages to localise core flows.",
        ],
      },
      {
        id: newId(),
        jobTitle: "UX Designer",
        company: "Freshworks",
        location: "Chennai",
        employmentType: "Full-time",
        startDate: monthIso(2016, 6),
        endDate: monthIso(2018, 6),
        isCurrent: false,
        description: "",
        bulletPoints: [
          "Owned the ticket detail view of Freshdesk, the product's most used screen.",
          "Partnered with engineering to ship keyboard-first triage, adopted by 30% of agents.",
        ],
      },
    ],
    education: [
      {
        id: newId(),
        institution: "National Institute of Design",
        degree: "M.Des",
        fieldOfStudy: "Interaction Design",
        location: "Ahmedabad",
        startDate: monthIso(2014, 7),
        endDate: monthIso(2016, 5),
        isCurrent: false,
        grade: "CGPA 8.9/10",
        description: "- Thesis: designing payments for first-time smartphone users",
      },
      {
        id: newId(),
        institution: "Anna University",
        degree: "B.E.",
        fieldOfStudy: "Computer Science",
        location: "Chennai",
        startDate: monthIso(2010, 7),
        endDate: monthIso(2014, 5),
        isCurrent: false,
        grade: "",
        description: "",
      },
    ],
    skills: [
      ["Product design", "Design"],
      ["Interaction design", "Design"],
      ["Design systems", "Design"],
      ["Prototyping", "Design"],
      ["User research", "Research"],
      ["Usability testing", "Research"],
      ["Figma", "Tools"],
      ["Framer", "Tools"],
      ["HTML & CSS", "Tools"],
    ].map(([name, category], i) => ({
      id: newId(),
      name,
      category,
      level: (i < 3 ? "expert" : i < 6 ? "advanced" : "intermediate") as SkillLevel,
    })),
    projects: [
      {
        id: newId(),
        name: "Saathi — voice-first banking",
        role: "Design lead",
        description:
          "Voice-first banking for first-time smartphone users, built with a non-profit partner.\n- 12k installs in the first month\n- Shortlisted for the India Design Awards",
        technologies: ["Figma", "Flutter", "Firebase"],
        link: "github.com/aanya/saathi",
        startDate: monthIso(2022, 1),
        endDate: monthIso(2022, 9),
        isCurrent: false,
      },
    ],
    certifications: [
      {
        id: newId(),
        name: "Google UX Design Certificate",
        issuer: "Google",
        issueDate: monthIso(2020, 5),
        credentialId: "UX-2020-1188",
        credentialUrl: "coursera.org/verify/ux1188",
      },
    ],
    languages: [
      { id: newId(), name: "English", proficiency: "native" },
      { id: newId(), name: "Hindi", proficiency: "fluent" },
      { id: newId(), name: "Tamil", proficiency: "conversational" },
    ],
    customSections: [
      {
        id: achievements,
        title: "Key Achievements",
        kind: "achievements",
        items: [
          { id: newId(), heading: "Spoke at Config India 2023 on designing for low-literacy users", subheading: "", location: "", description: "", url: "", date: null, endDate: null, isCurrent: false },
          { id: newId(), heading: "Grew the design team from 3 to 11 people in 18 months", subheading: "", location: "", description: "", url: "", date: null, endDate: null, isCurrent: false },
        ],
      },
      {
        id: volunteering,
        title: "Volunteering",
        kind: "volunteering",
        items: [
          {
            id: newId(),
            heading: "Design mentor",
            subheading: "ADPList",
            location: "Remote",
            description: "- Mentored 60+ early-career designers",
            url: "",
            date: monthIso(2020, 1),
            endDate: null,
            isCurrent: true,
          },
        ],
      },
      {
        id: interests,
        title: "Interests",
        kind: "interests",
        items: ["Trail running", "Letterpress printing", "Carnatic music"].map((heading) => ({
          id: newId(),
          heading,
          subheading: "",
          location: "",
          description: "",
          url: "",
          date: null,
          endDate: null,
          isCurrent: false,
        })),
      },
    ],
    sectionOrder: ["summary", "experience", "projects", "education", "skills", "certifications", "languages", achievements, volunteering, interests],
  };
}
